import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { QUICK_REPLY_EMOJIS } from '@arthelper/shared/dm-quick-replies';

interface DmQuickRepliesProps {
  /** Phrase suggestions (curated fallback, or AI context-aware when available). */
  phrases: string[];
  /** Send a reply immediately with the tapped text. */
  onSend: (text: string) => void;
  emojis?: string[];
  disabled?: boolean;
}

const ARROW_WRAP =
  'pointer-events-none absolute top-0 bottom-2 z-10 flex items-center from-white via-white to-transparent';
const ARROW_BUTTON =
  'pointer-events-auto hidden size-7 items-center justify-center rounded-full md:flex border border-gray-200 bg-white text-gray-600 shadow-xs hover:bg-gray-50 transition-colors';

/**
 * One-tap quick replies shown above the DM composer: a single scrolling row of emoji
 * tapbacks followed by short positive phrase chips. Prev/next arrows appear only while
 * the row overflows in that direction (desktop only; touch swipes), so every chip stays
 * reachable in a tight composer.
 * Tapping any chip sends it right away.
 * Twin of town-square/src/components/messaging/DmQuickReplies.tsx — edit both.
 */
export function DmQuickReplies({ phrases, onSend, emojis = QUICK_REPLY_EMOJIS, disabled }: DmQuickRepliesProps) {
  const { t } = useTranslation('social');
  const scrollRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    // Observe the track too: AI phrases arrive after mount and widen it without resizing the viewport.
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    if (trackRef.current) {
      observer.observe(trackRef.current);
    }
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      observer.disconnect();
    };
  }, [updateScrollState]);

  const scroll = (direction: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: 'smooth' });
  };

  return (
    <div className="relative pb-2">
      {canScrollLeft && (
        <div className={`${ARROW_WRAP} left-0 bg-gradient-to-r pr-4`}>
          <button
            type="button"
            onClick={() => scroll(-1)}
            aria-label={t('dm.quick-replies.previous')}
            className={ARROW_BUTTON}
          >
            <ChevronLeft className="size-4" />
          </button>
        </div>
      )}
      <div ref={scrollRef} className="overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div ref={trackRef} className="flex w-max items-center gap-1.5 whitespace-nowrap">
          {emojis.map((emoji) => (
            <button
              key={emoji}
              type="button"
              disabled={disabled}
              onClick={() => onSend(emoji)}
              aria-label={`Send ${emoji}`}
              className="flex-shrink-0 size-8 rounded-full border border-gray-200 bg-white text-lg leading-none hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              {emoji}
            </button>
          ))}
          {phrases.map((phrase) => (
            <button
              key={phrase}
              type="button"
              disabled={disabled}
              onClick={() => onSend(phrase)}
              className="flex-shrink-0 rounded-full border border-gray-200 bg-white px-3 h-8 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              {phrase}
            </button>
          ))}
        </div>
      </div>
      {canScrollRight && (
        <div className={`${ARROW_WRAP} right-0 bg-gradient-to-l pl-4`}>
          <button
            type="button"
            onClick={() => scroll(1)}
            aria-label={t('dm.quick-replies.next')}
            className={ARROW_BUTTON}
          >
            <ChevronRight className="size-4" />
          </button>
        </div>
      )}
    </div>
  );
}
